/**
 * Centralized SEO helper for GENI
 * Ensures clean management of document title, meta descriptions, canonical URLs,
 * hreflang alternates (en, pt-BR, x-default), and documentElement lang.
 */

const BASE_URL = 'https://genitradeapp.com';

export const ARTICLE_PAIRS = {
  '/what-is-geni': '/pt/o-que-e-o-geni',
  '/automated-trading-journal': '/pt/diario-de-trades-automatico',
  '/crypto-trading-journal': '/pt/diario-de-trades-cripto',
  '/how-to-analyze-your-trades': '/pt/como-analisar-trades',
  '/manual-vs-automated-trading-journal': '/pt/diario-de-trades-manual-vs-automatico',
  '/why-traders-fail-to-improve': '/pt/por-que-traders-nao-evoluem',
  '/how-to-improve-day-trading': '/pt/como-melhorar-no-day-trade',
  '/profit-factor-trading': '/pt/profit-factor-trading',
  '/win-rate-trading': '/pt/win-rate-trading',
  '/overtrading-explained': '/pt/overtrading',
  '/how-many-trades-to-validate-a-strategy': '/pt/quantos-trades-para-validar-estrategia',
  '/how-to-find-a-trading-setup': '/pt/como-encontrar-um-setup',
  '/improve-risk-reward-ratio': '/pt/como-melhorar-risk-reward',
  '/why-consistency-is-hard-in-trading': '/pt/por-que-consistencia-e-dificil-no-trading',
};

// Reverse map: PT path -> EN path
export const PT_TO_EN_PAIRS = Object.entries(ARTICLE_PAIRS).reduce((acc, [en, pt]) => {
  acc[pt] = en;
  return acc;
}, {});

export const updateSeoMetadata = ({
  title,
  description,
  path,
  lang = 'en',
  alternates = null, // { en: '/path-en', 'pt-BR': '/pt/path-pt', 'x-default': '/path-en' }
}) => {
  if (typeof document === 'undefined') return;

  // 1. Update HTML Lang
  document.documentElement.lang = lang === 'pt' || lang === 'pt-BR' ? 'pt-BR' : 'en';

  // 2. Update Title
  if (title) {
    document.title = title;
  }

  // 3. Update Meta Description
  if (description) {
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);
  }

  // 4. Update Canonical URL (Single canonical instance)
  const canonicalUrl = path.startsWith('http') ? path : `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
  existingCanonicals.forEach((el, index) => {
    if (index > 0) el.remove();
  });

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);

  // 5. Update Hreflang alternates
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

  if (alternates) {
    Object.entries(alternates).forEach(([hreflang, altPath]) => {
      if (!altPath) return;
      const fullAltUrl = altPath.startsWith('http') ? altPath : `${BASE_URL}${altPath.startsWith('/') ? altPath : `/${altPath}`}`;
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = fullAltUrl;
      document.head.appendChild(link);
    });
  }
};

export const getArticleAlternates = (currentPath) => {
  const cleanPath = currentPath.split('?')[0].split('#')[0];
  if (ARTICLE_PAIRS[cleanPath]) {
    const ptPath = ARTICLE_PAIRS[cleanPath];
    return {
      'en': cleanPath,
      'pt-BR': ptPath,
      'x-default': cleanPath,
    };
  }
  if (PT_TO_EN_PAIRS[cleanPath]) {
    const enPath = PT_TO_EN_PAIRS[cleanPath];
    return {
      'en': enPath,
      'pt-BR': cleanPath,
      'x-default': enPath,
    };
  }
  return null;
};